import React, { useState, useEffect } from "react";
import { fetchOne } from "../utils/fetchResources";
import { Link, useParams, Prompt } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import { onReadyCKE, CKEconfig } from "../config/CKE";
import { useSnackbar } from "notistack";
import {
	makeStyles,
	Typography,
	TextField,
	Button,
	Grid,
	Card,
	CardContent,
	Hidden,
	useTheme,
	FormGroup,
	FormControlLabel,
	Switch,
} from "@material-ui/core";
import SetFile from "../components/SetFile";

function SitesEdit(props) {
	const theme = useTheme();
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [unsaved, setUnsaved] = useState(false);
	const [site, setSite] = useState();
	const { enqueueSnackbar } = useSnackbar();

	useEffect(() => {
		(async () => {
			await fetchOne("sites", id, setSite);
			setLoading(false);
		})();
	}, [id]);

	const onSubmit = (e) => {
		e.preventDefault();
		fetch(`${process.env.REACT_APP_API_URL}/sites/${id}`, {
			method: "PUT",
			credentials: "include",
			body: JSON.stringify(site),
			headers: { "Content-Type": "application/json" },
		})
			.then((res) => res.json())
			.then((data) => {
				console.log(data);
				if (data.success) {
					setUnsaved(false);
					enqueueSnackbar("Site updated successfully!", { variant: "success" });
					fetchOne("sites", id, setSite);
				} else {
					console.log(data);
					const errorArray = data.error.split(",");
					errorArray.forEach((error) => {
						enqueueSnackbar(`${error}`, { variant: "error" });
					});
				}
			});
	};

	const onChange = (e) => {
		setSite({ ...site, [e.target.name]: e.target.value });
		setUnsaved(true);
	};

	const onChangePublished = (e) => {
		setSite({ ...site, [e.target.name]: e.target.checked });
		setUnsaved(true);
	};

	const onChangeDescription = (e, editor) => {
		const data = editor.getData();
		setSite({ ...site, description: data });
		setUnsaved(true);
	};

	const onChangeWarning = (e, editor) => {
		const data = editor.getData();
		setSite({ ...site, warning: data });
		setUnsaved(true);
	};

	const useStyles = makeStyles((theme) => ({
		paper: {
			marginTop: theme.spacing(8),
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
		},
		form: {
			width: "100%", // Fix IE 11 issue.
		},
		submit: {
			margin: theme.spacing(2, 0, 1),
		},
		CKElabels: {
			marginTop: "1.5rem",
			fontWeight: "bold",
		},
		colorSettings: {
			listStyleType: "none",
			marginTop: "1rem",
			padding: 0,
			"& li": {
				margin: ".5rem 0",
			},
		},
		colorLabel: {
			marginLeft: ".5rem",
		},
	}));

	const classes = useStyles();

	if (loading) {
		return <div>Loading...</div>;
	}

	return (
		<>
			<Prompt
				when={unsaved}
				message="You have unsaved changes. Are you sure you want to leave?"
			/>
			<form className={classes.form} onSubmit={onSubmit}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={12} md={9}>
						<Typography component="h1" variant="h4">
							Edit Site:{" "}
							<Link
								style={{ color: theme.palette.primary.main }}
								to={`/${site.slug}`}
								target="_blank"
							>
								{site.name}
							</Link>
						</Typography>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							id="name"
							label="Site Name"
							name="name"
							size="small"
							required
							onChange={onChange}
							value={site.name}
						/>
						<TextField
							variant="outlined"
							margin="normal"
							fullWidth
							required
							id="slug"
							label="Slug"
							name="slug"
							size="small"
							onChange={onChange}
							value={site.slug}
						/>
						<p className={classes.CKElabels}>Description*:</p>
						<CKEditor
							onReady={(editor) => {
								onReadyCKE(editor);
							}}
							editor={Editor}
							onChange={onChangeDescription}
							data={site.description}
							config={CKEconfig("Short site description...")}
						/>
						<p className={classes.CKElabels}>Warning:</p>
						<CKEditor
							onReady={(editor) => {
								onReadyCKE(editor);
							}}
							editor={Editor}
							onChange={onChangeWarning}
							data={site.warning}
							config={CKEconfig("Warning text (optional)...")}
						/>
						<Button
							type="submit"
							fullWidth
							variant="contained"
							color="primary"
							className={classes.submit}
						>
							Save
						</Button>
					</Grid>
					<Grid item xs={12} sm={12} md={3}>
						<Card>
							<CardContent>
								<Hidden smDown>
									<Button
										type="submit"
										onClick={onSubmit}
										fullWidth
										variant="contained"
										color="primary"
										className={classes.submit}
									>
										Save
									</Button>
								</Hidden>
								<FormGroup row>
									<FormControlLabel
										control={
											<Switch
												color="primary"
												checked={site.published ?? false}
												onChange={onChangePublished}
												name="published"
											/>
										}
										label={
											site.published ? (
												<Typography color="primary">Published</Typography>
											) : (
												<Typography color="secondary">Draft</Typography>
											)
										}
									/>
								</FormGroup>
								<SetFile
									fileTitle="Featured Image"
									resource={site}
									setResource={setSite}
									imageOnly={true}
									fileLabel="featuredImage"
									fileLabelFileName="featuredImageFile"
								/>
								<SetFile
									fileTitle="Banner Image"
									resource={site}
									setResource={setSite}
									imageOnly={true}
									fileLabel="bannerImage"
									fileLabelFileName="bannerImageFile"
								/>
								<SetFile
									fileTitle="Regions Image"
									resource={site}
									setResource={setSite}
									imageOnly={true}
									fileLabel="regionsImage"
									fileLabelFileName="regionsImageFile"
								/>
								<Typography
									variant="h6"
									component="span"
									style={{ marginTop: "1.5rem" }}
								>
									Color Settings
								</Typography>
								<ul className={classes.colorSettings}>
									<li>
										<input
											id="primary-color"
											name="primaryColor"
											type="color"
											value={site ? site.primaryColor : null}
											onChange={onChange}
										/>
										<label
											className={classes.colorLabel}
											htmlFor="primary-color"
										>
											Primary Color
										</label>
									</li>
									<li>
										<input
											id="secondary-color"
											name="secondaryColor"
											type="color"
											value={site ? site.secondaryColor : null}
											onChange={onChange}
										/>
										<label
											className={classes.colorLabel}
											htmlFor="secondary-color"
										>
											Secondary Color
										</label>
									</li>
								</ul>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</form>
		</>
	);
}

export default SitesEdit;
